import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getPaymentReceipts, previewPaymentReceipt, resendPaymentReceipt, sendPaymentReceipt, RECEIPT_METHODS } from '../lib/api';
import type { PaymentReceipt, PaymentRecord, ReceiptInput } from '../lib/api';

const rupees  = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDay  = (d: string) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtWhen = (iso: string | null) => iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Send a fee receipt, from the Payments page ───────────────────────────────
// Admin types in what the student paid, and when and how; the PDF receipt is
// emailed to the student (admin gets a copy). "Add to their payments" records
// the amount at the same time — untick it when it's already in through Update
// Payment, which is how the toast after Update Payment opens this. Receipts
// sent before are listed underneath, and each can be sent again.

const ReceiptModal: React.FC<{
  record: PaymentRecord;
  adminPassword: string;
  today: string;
  amount?: number;            // prefilled, from the Update Payment toast
  alreadyRecorded?: boolean;  // the payment is already in their totals
  onClose: () => void;
  onSent: (rowIndex: string, payment: { balancePaid: number; totalPaid: number; courseFee: number }) => void;
}> = ({ record, adminPassword, today, amount: presetAmount, alreadyRecorded, onClose, onSent }) => {
  const first = record.name.split(' ')[0] || record.name;
  const [amount, setAmount]       = useState(presetAmount ? String(presetAmount) : '');
  const [paidOn, setPaidOn]       = useState(today);
  const [method, setMethod]       = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote]           = useState('');
  const [email, setEmail]         = useState(record.studentEmail || record.email || '');
  const [addToPayments, setAddToPayments] = useState(!alreadyRecorded);
  const [busy, setBusy]           = useState('');   // 'preview' | 'send' | the _id of a receipt being sent again
  const [history, setHistory]     = useState<PaymentReceipt[] | null>(null);

  useEffect(() => {
    getPaymentReceipts(adminPassword, record.rowIndex)
      .then(res => setHistory(res.receipts))
      .catch(() => setHistory([]));   // only a help — the form works without it
  }, [adminPassword, record.rowIndex]);

  const amt          = Math.round(Number(amount) || 0);
  const totalAfter   = record.totalPaid + (addToPayments ? amt : 0);
  const balanceAfter = Math.max(0, record.courseFee - totalAfter);
  const blocked = !(record.courseFee > 0) ? `Set ${first}'s course fee first (Update Payment) — the receipt shows the fee and what's left to pay.` : '';
  const tooMuch = !addToPayments && amt > record.totalPaid;   // the server refuses this too
  const sameAsBefore = amt > 0 && history?.find(h => h.amount === amt && h.paidOn === paidOn);
  const warnings = [
    addToPayments && amt > 0 && record.balance === 0 && `${first} has already paid the full fee — this takes their total above it.`,
    addToPayments && amt > record.balance && record.balance > 0 && `That's ${rupees(amt - record.balance)} more than the ${rupees(record.balance)} still to pay.`,
    sameAsBefore && `A receipt for ${rupees(amt)} paid on ${fmtDay(paidOn)} was already sent (#${sameAsBefore.receiptNo}) — use Send again below if ${first} needs another copy.`,
  ].filter(Boolean) as string[];

  const buildInput = (): ReceiptInput | null => {
    if (amt < 1) { toast.error('Enter the amount received'); return null; }
    if (!paidOn) { toast.error('Pick the date the payment was made'); return null; }
    if (tooMuch) { toast.error(`${first}'s recorded payments only add up to ${rupees(record.totalPaid)} — tick "Add to their payments"`); return null; }
    return {
      paymentId: record.rowIndex, amount: amt, paidOn, method, reference: reference.trim(),
      note: note.trim(), email: email.trim(), record: addToPayments,
    };
  };

  const handlePreview = async () => {
    const input = buildInput();
    if (!input) return;
    // Open the tab now, while we still have the click — popup blockers stop
    // window.open() calls made after an await.
    const win = window.open('', '_blank');
    setBusy('preview');
    try {
      const res = await previewPaymentReceipt(adminPassword, input);
      const bytes = Uint8Array.from(atob(res.pdfBase64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) { win?.close(); toast.error(err.message); }
    finally { setBusy(''); }
  };

  const handleSend = async () => {
    const input = buildInput();
    if (!input) return;
    if (!EMAIL_RE.test(input.email)) { toast.error(`Enter ${first}'s email address`); return; }
    const ok = window.confirm(
      `Email a receipt for ${rupees(amt)} to ${record.name} (${input.email})?\n\n` +
      (addToPayments
        ? `${rupees(amt)} will be added to their payments — total paid ${rupees(record.totalPaid)} → ${rupees(totalAfter)}.`
        : `Their payments stay as they are (total paid ${rupees(record.totalPaid)}).`));
    if (!ok) return;
    setBusy('send');
    try {
      const res = await sendPaymentReceipt(adminPassword, input);
      toast.success(`Receipt for ${rupees(amt)} emailed to ${res.sentTo}`);
      onSent(record.rowIndex, res.payment);
      onClose();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(''); }
  };

  const handleResend = async (r: PaymentReceipt) => {
    const to = email.trim() || r.email;
    if (!EMAIL_RE.test(to)) { toast.error('Enter a valid email address in "Send to"'); return; }
    if (!window.confirm(`Send receipt #${r.receiptNo} (${rupees(r.amount)}) again to ${to}?`)) return;
    setBusy(r._id);
    try {
      const res = await resendPaymentReceipt(adminPassword, r._id, to);
      toast.success(`Receipt #${r.receiptNo} sent again to ${res.sentTo}`);
      setHistory(prev => prev && prev.map(h => (h._id === r._id ? res.receipt : h)));
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(''); }
  };

  const inputCls = 'w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400';
  const caption  = 'block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
          <div>
            <h3 className="font-bold text-white">🧾 Send receipt</h3>
            <p className="text-xs text-white/60 mt-0.5">{record.name}{record.phone ? ` · ${record.phone}` : ''}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg leading-none">×</button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto space-y-4">
          <div className="rounded-xl border border-gray-100 p-3.5 grid grid-cols-3 gap-3 text-center" style={{ background: '#f8f6ff' }}>
            {[
              { label: 'Course fee', value: rupees(record.courseFee), color: '#2b2240' },
              { label: 'Paid so far', value: rupees(record.totalPaid), color: '#16a34a' },
              { label: 'Still to pay', value: record.balance === 0 ? '✓ Clear' : rupees(record.balance), color: record.balance === 0 ? '#16a34a' : '#dc2626' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{s.label}</div>
                <div className="text-sm font-bold" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {blocked ? (
            <p className="text-sm text-gray-600 bg-gray-50 rounded-xl px-4 py-3">{blocked}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={caption}>Amount received (₹)</span>
                  <input type="number" min="1" step="1" value={amount} onChange={e => setAmount(e.target.value)} autoFocus
                    placeholder={record.balance > 0 ? `e.g. ${record.balance}` : 'e.g. 5000'}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-base font-bold focus:outline-none focus:border-yellow-400" />
                </label>
                <label className="block">
                  <span className={caption}>Paid on</span>
                  <input type="date" value={paidOn} min="2020-01-01" max={today} onChange={e => setPaidOn(e.target.value)} className={inputCls} />
                </label>
              </div>

              <div>
                <span className={caption}>Paid by <span className="normal-case tracking-normal font-normal">(optional)</span></span>
                <div className="flex flex-wrap gap-1.5">
                  {RECEIPT_METHODS.map(m => (
                    <button key={m} type="button" onClick={() => setMethod(cur => (cur === m ? '' : m))} aria-pressed={method === m}
                      className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-colors"
                      style={method === m
                        ? { background: '#2b2240', color: '#F0B429', borderColor: '#2b2240' }
                        : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className={caption}>Reference / transaction ID <span className="normal-case tracking-normal font-normal">(optional)</span></span>
                <input value={reference} onChange={e => setReference(e.target.value)} maxLength={100}
                  placeholder="UPI ref no., cheque no.…" className={inputCls} />
              </label>

              <label className="flex items-start gap-3 rounded-xl border-2 p-3.5 cursor-pointer"
                style={{ borderColor: addToPayments ? '#2b2240' : '#e5e7eb', background: addToPayments ? '#f8f6ff' : '#fff' }}>
                <input type="checkbox" checked={addToPayments} onChange={e => setAddToPayments(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#2b2240]" />
                <span className="text-sm text-gray-700 leading-snug">
                  <b>Add {amt > 0 ? rupees(amt) : 'this amount'} to {first}'s payments</b>
                  <span className="block text-xs text-gray-500 mt-1">
                    {addToPayments
                      ? <>Total paid {rupees(record.totalPaid)} → <b className="text-gray-700">{rupees(totalAfter)}</b> · still to pay <b className={balanceAfter ? 'text-red-600' : 'text-green-600'}>{balanceAfter ? rupees(balanceAfter) : '✓ nothing'}</b></>
                      : <>Untick when it's already recorded with Update Payment — the receipt then shows their totals as they are: paid {rupees(record.totalPaid)}, {record.balance ? `${rupees(record.balance)} to pay` : 'nothing left to pay'}.</>}
                  </span>
                </span>
              </label>

              {tooMuch && (
                <p className="text-xs rounded-xl px-3.5 py-2.5" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                  {first}'s recorded payments only add up to {rupees(record.totalPaid)}, so this payment can't be in them yet — tick "Add to their payments".
                </p>
              )}
              {warnings.map(w => (
                <p key={w} className="text-xs rounded-xl px-3.5 py-2.5" style={{ background: '#fef3c7', color: '#92400e' }}>⚠️ {w}</p>
              ))}

              <label className="block">
                <span className={caption}>Send to</span>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="student@email.com" className={inputCls} />
              </label>
              <label className="block">
                <span className={caption}>Note to {first} <span className="normal-case tracking-normal font-normal">(optional — goes in the email)</span></span>
                <input value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="e.g. Thank you for paying on time!" className={inputCls} />
              </label>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={handlePreview} disabled={!!busy}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                  {busy === 'preview' ? 'Opening…' : 'Preview PDF'}
                </button>
                <button type="button" onClick={handleSend} disabled={!!busy || tooMuch}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#2b2240,#5a4a6f)' }}>
                  {busy === 'send' ? 'Sending…' : '📧 Send receipt'}
                </button>
              </div>
              <p className="text-[11px] text-gray-400 -mt-2 leading-relaxed">
                {first} gets the PDF receipt by email; you get a copy at vistatech.in@gmail.com.
              </p>
            </>
          )}

          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-bold text-gray-700 mb-2">Receipts sent to {first}</p>
            {history === null ? (
              <p className="text-xs text-gray-400">Loading…</p>
            ) : history.length === 0 ? (
              <p className="text-xs text-gray-400">None yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map(h => (
                  <div key={h._id} className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold" style={{ color: '#2b2240' }}>
                        {rupees(h.amount)} <span className="text-xs font-semibold text-gray-500">paid {fmtDay(h.paidOn)}{h.method ? ` · ${h.method}` : ''}</span>
                      </p>
                      <p className="text-[11px] text-gray-400 truncate" title={h.email}>
                        #{h.receiptNo} · {h.source === 'website' ? 'from the website form' : 'sent'} {fmtWhen(h.lastSentAt || h.issuedAt)} to {h.email}
                        {h.sendCount > 1 ? ` · sent ${h.sendCount}×` : ''}
                      </p>
                    </div>
                    <button onClick={() => handleResend(h)} disabled={!!busy}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-bold border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50">
                      {busy === h._id ? 'Sending…' : 'Send again'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
